import React, { FunctionComponent } from "react";
import styles from "./TextImage.module.scss";
import TextImageBlockProps from "./TextImageProps.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getImageCrop from "app/media/helpers/getImageCrop";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import IconViewer from "components/IconViewer/IconViewer";
import ImageViewer from "components/ImageViewer/ImageViewer";
import Paragraph from "components/Paragraph/Paragraph";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useAnchor from "blocks/hooks/useAnchor";
import useAnimation from "blocks/hooks/useAnimation";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";

const TextImage: FunctionComponent<TextImageBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);
    const { animationClasses } = useAnimation(styles, "animation1");

    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;

    const imageCrop = getImageCrop(props.content.image?.umbracoFile.crops, props.settings.imageCrop, "Fullscreen (4:3)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props.content.image?.umbracoWidth, props.content.image?.umbracoHeight);
    const imagePositionClass = props.settings.imagePosition === "Media Right" ? ` ${styles.has_image_right}` : undefined;
    const imageFit = props.settings.imageFit === "Contain" ? "contain" : "cover";

    return (
        <section className={combineClasses(styles.wrapper, ...animationClasses, inViewClasses, ...wrapperClasses)} style={{ ...wrapperStyles, ...splitStyles }} id={identifier} ref={inViewElementRef}>
            <div className={combineClasses(styles.container, ...splitClasses, imagePositionClass)}>
                <div className={styles.media}>
                    {/* Image */}
                    {props.content.image ? (
                        <ImageViewer
                            alt={props.content.imageAlternative}
                            aspectRatioX={imageAspectRatio.aspectRatioX}
                            aspectRatioY={imageAspectRatio.aspectRatioY}
                            className={styles.image}
                            loadedClassName={styles.is_loaded}
                            crop={imageCrop}
                            extensionType={props.content.image.umbracoExtension}
                            focalPoint={props.content.image.umbracoFile.focalPoint}
                            key={`img_${props.contentUdi}`}
                            sourceHeight={props.content.image?.umbracoHeight}
                            sourceWidth={props.content.image?.umbracoWidth}
                            src={props.content.image.umbracoFile.src}
                            type={imageFit} />
                    ) : null}

                    {/* Caption */}
                    {props.content.imageCaption && props.content.imageCaption.length > 0 ? (
                        <div className={styles.caption}>{props.content.imageCaption}</div>
                    ) : null}
                </div>

                <div className={styles.content}>

                    {/* Icon */}
                    {props.content.icon ? (
                        <IconViewer className={styles.icon} name={props.content.icon.toLowerCase()} />
                    ) : null}

                    {/* Tagline */}
                    {props.content.tagline ? (
                        <span className={styles.tagline}>{props.content.tagline}</span>
                    ) : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading key={`head_${props.contentUdi}`} className={combineClasses(styles.headline, headlineCaseClass)} text={props.content.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight} />
                    ) : null}

                    {/* Text */}
                    {props.content.bodyText ? (
                        <Paragraph key={`para_${props.contentUdi}`} className={styles.text} innerHTML={props.content.bodyText} />
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button) => (
                                <Button key={button.contentUdi} to={button.content.link} className={styles.button} variant={button.content.variant} {...button.content}>{button.content.label}</Button>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default TextImage;